import * as React from 'react'
import {
  CYPHER_WRITE_BLOCKED_LABELS,
  GRAPH_NODE_TYPE_REGISTRY,
  humanizeUrn,
  type NodeLabel
} from '@atbash-labs/ontology-core'
import {
  collectAdditionalProperties,
  usePlantSessionPaint,
  type PlantEntityResult
} from '@axilon-apps/domains-ontology-ui-state'
import { Badge } from '../../../components/ui/badge'
import { registryIconFor } from '../registry-icons'
import { SessionTitleMark, sessionDetailHeaderClass } from '../session-mark-ui'
import { EntityDetailsInspector } from './EntityDetailsInspector'
import { PlantStrayPill } from './PlantStrayPill'
import { TypedPropertiesSection } from './TypedPropertiesSection'

/**
 * Schema-driven detail page for entities without a bespoke surface.
 * Follows the bespoke detail-page grammar (EquipmentDetailPage et al.):
 * icon-tile header with badge row and mono id, then stacked sections in
 * the shared card treatment.
 */
export function GenericEntityPage({
  result,
  label
}: {
  result: PlantEntityResult
  label: NodeLabel | null
}): React.ReactElement {
  const { entity } = result
  const registry = label ? GRAPH_NODE_TYPE_REGISTRY[label] : null
  const displayValue = registry ? entity[registry.displayLabelField] : undefined
  const title =
    (typeof displayValue === 'string' && displayValue.trim()) ||
    (entity.name !== entity.id ? entity.name : '') ||
    humanizeUrn(entity.id)
  const raw = entity as Record<string, unknown>
  const additionalProperties = label ? collectAdditionalProperties(label, raw) : []
  const Icon = registryIconFor(label)
  const { markFor } = usePlantSessionPaint()
  const entityMark = markFor(entity.id)

  return (
    <div className="flex min-w-0 flex-col gap-card">
      <header
        className={sessionDetailHeaderClass(
          'flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4',
          entityMark
        )}
      >
        <span className="grid size-12 shrink-0 place-items-center rounded-xl bg-brand-soft text-brand">
          <Icon size={24} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="m-0 min-w-0 break-words text-2xl font-semibold text-ink">{title}</h1>
            <SessionTitleMark mark={entityMark} />
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <Badge variant="outline">{label ?? result.labels[0] ?? 'Unregistered'}</Badge>
            {registry && <Badge variant="secondary">{registry.domainGroup}</Badge>}
            {label && CYPHER_WRITE_BLOCKED_LABELS.has(label) && (
              <Badge variant="outline">Read-only schema</Badge>
            )}
            <PlantStrayPill isStray={label != null && result.strayLabels.includes(label)} />
          </div>
          <div className="mt-1.5 break-all font-mono text-11 text-ink-4">{entity.id}</div>
        </div>
      </header>

      {!label && (
        <div className="rounded-xl border bg-card p-4 text-sm text-ink-3 shadow-sm">
          This entity has no registered ontology label. Raw identity and provenance are still
          available.
        </div>
      )}

      {label && <TypedPropertiesSection label={label} entity={raw} />}
      <EntityDetailsInspector entity={entity} additionalProperties={additionalProperties} />
    </div>
  )
}
